import type { ReactNode } from 'react'
import { Button } from './Button.tsx'
import { Input } from './Input.tsx'

/**
 * How an entity is shown and edited.
 *
 * A filial, a product: mostly what the ERP says, shown as labelled fields
 * and disabled, plus the one or two fields that are ours, enabled, with a
 * save button at the bottom. One shape for all of them, so a person who has
 * seen one knows where the editable part is and where the save is.
 *
 * `Field` is a label over a disabled input by default -- disabled rather
 * than plain text, so a read-only value looks like a value and sits in the
 * same grid as the editable ones. Anything richer than an input (a group of
 * checkboxes) goes in as `children` under its own label.
 */
export function EntityForm({
  title,
  description,
  children,
  onSave,
  saving = false,
  dirty = false,
  saveLabel = 'Salvar',
}: {
  title?: ReactNode
  description?: ReactNode
  children: ReactNode
  /** Absent means nothing here is editable, and no save button is drawn. */
  onSave?: (() => void) | undefined
  saving?: boolean
  dirty?: boolean
  saveLabel?: string
}) {
  return (
    <form
      className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-4 border p-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSave?.()
      }}
    >
      {(title !== undefined || description !== undefined) && (
        <div className="flex flex-col gap-1">
          {title !== undefined && <h2 className="text-tPSemi text-text-strong">{title}</h2>}
          {description !== undefined && (
            <p className="text-tSmall text-text-weak max-w-[70ch]">{description}</p>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">{children}</div>
      {onSave !== undefined && (
        <div className="border-stroke-weak flex justify-end border-t pt-4">
          <Button
            type="submit"
            variant="primary"
            tone="brand"
            size="small"
            disabled={saving || !dirty}
          >
            {saving ? 'Salvando…' : saveLabel}
          </Button>
        </div>
      )}
    </form>
  )
}

/** One labelled value. Disabled unless something says otherwise. */
export function Field({
  label,
  value,
  editable = false,
  onChange,
  helperText,
  wide = false,
}: {
  label: ReactNode
  value: string
  editable?: boolean
  onChange?: (value: string) => void
  helperText?: ReactNode
  /** Spans both columns. */
  wide?: boolean
}) {
  return (
    <Input
      label={label}
      value={value}
      disabled={!editable}
      readOnly={!editable && onChange === undefined}
      {...(onChange === undefined ? {} : { onChange: (event) => onChange(event.target.value) })}
      {...(helperText === undefined ? {} : { helperText })}
      {...(wide ? { containerClassName: 'md:col-span-2' } : {})}
    />
  )
}

/** A labelled slot for anything that is not a plain input. */
export function FieldGroup({
  label,
  helperText,
  children,
  wide = true,
}: {
  label: ReactNode
  helperText?: ReactNode
  children: ReactNode
  wide?: boolean
}) {
  return (
    <div className={`flex flex-col gap-2 ${wide ? 'md:col-span-2' : ''}`}>
      <span className="text-tP text-text-strong">{label}</span>
      {children}
      {helperText !== undefined && <span className="text-tSmall text-text-weak">{helperText}</span>}
    </div>
  )
}
